import { useState, useMemo } from "react";
import { Input } from "@tellescope/design-system";
import { Button, cn } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import type { Snippet } from "../../../shared/schema";

interface SnippetListProps {
  snippets: Snippet[];
  onAddSnippet: () => void;
  onUpdateSnippet: (snippetId: string, updates: Partial<Omit<Snippet, "id">>) => void;
  onRemoveSnippet: (snippetId: string) => void;
}

interface SnippetItemProps {
  snippet: Snippet;
  isDuplicateKey: boolean;
  onUpdate: (updates: Partial<Omit<Snippet, "id">>) => void;
  onRemove: () => void;
}

// Regex for valid snippet key: starts with letter, alphanumeric/underscore only
const KEY_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

function SnippetItem({ snippet, isDuplicateKey, onUpdate, onRemove }: SnippetItemProps) {
  const { t } = useTranslation();

  const isKeyValid = KEY_REGEX.test(snippet.key);
  const hasKeyError = !isKeyValid || isDuplicateKey;

  return (
    <div className="group flex items-center gap-2">
      {/* Key input */}
      <Input
        value={snippet.key}
        onChange={(e) => onUpdate({ key: e.target.value })}
        placeholder={t("key")}
        className={cn("h-8 w-32 font-mono text-sm", hasKeyError && "border-destructive focus-visible:ring-destructive")}
        title={
          isDuplicateKey
            ? t("Duplicate key")
            : !isKeyValid
            ? t("Key must start with letter, alphanumeric/underscore only")
            : undefined
        }
      />

      {/* Value input */}
      <Input
        value={snippet.value}
        onChange={(e) => onUpdate({ value: e.target.value })}
        placeholder={t("value")}
        className="h-8 flex-1 text-sm"
      />

      {/* Delete button */}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 flex-shrink-0 hover:bg-destructive/10 hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export const SnippetList: React.FC<SnippetListProps> = ({
  snippets,
  onAddSnippet,
  onUpdateSnippet,
  onRemoveSnippet,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Track duplicate keys
  const duplicateKeys = useMemo(() => {
    const keyCounts = new Map<string, number>();
    for (const s of snippets) {
      keyCounts.set(s.key, (keyCounts.get(s.key) ?? 0) + 1);
    }
    return new Set(
      [...keyCounts.entries()].reduce<string[]>((acc, [key, count]) => {
        if (count > 1) {
          acc.push(key);
        }
        return acc;
      }, [])
    );
  }, [snippets]);

  return (
    <div className="space-y-2">
      {/* Collapsible header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {t("Snippets")}
          {snippets.length > 0 && <span className="ml-1 text-xs text-muted-foreground/70">({snippets.length})</span>}
        </button>

        {isExpanded && (
          <Button variant="ghost" size="sm" onClick={onAddSnippet}>
            <Plus className="h-4 w-4 mr-1" />
            {t("Add")}
          </Button>
        )}
      </div>

      {/* Snippet list (when expanded) */}
      {isExpanded && (
        <div className="space-y-2 pl-5">
          {snippets.map((snippet) => (
            <SnippetItem
              key={snippet.id}
              snippet={snippet}
              isDuplicateKey={duplicateKeys.has(snippet.key)}
              onUpdate={(updates) => onUpdateSnippet(snippet.id, updates)}
              onRemove={() => onRemoveSnippet(snippet.id)}
            />
          ))}

          {snippets.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">
              {t("No snippets yet. Add one to create reusable text like {{snippet.yourKey}}.")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
